import { useEffect, useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Bell, ShieldCheck, BookOpen, BarChart3, CheckCircle2, XCircle,
  AlertTriangle, Archive, RefreshCw, Loader2, Send, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

// Centro de notificaciones fiscales — feed unificado de los 3 canales de
// email (Corte-Z certificate push, libro anual, resumen mensual) con KPIs
// de entregabilidad y botones de reenvío por fila.

const TYPE_META = {
  z_cert: { label: "Certificado Z", icon: ShieldCheck, color: "#7c3aed" },
  annual_book: { label: "Libro anual", icon: BookOpen, color: "#059669" },
  monthly_summary: { label: "Resumen mensual", icon: BarChart3, color: "#2563eb" },
};

const STATUS_META = {
  sent: { label: "Enviado", icon: CheckCircle2, bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
  failed: { label: "Falló", icon: XCircle, bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700" },
  skipped: { label: "Omitido", icon: AlertTriangle, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
  superseded: { label: "Superpuesto", icon: Archive, bg: "bg-muted/50", border: "border-border", text: "text-muted-foreground" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.skipped;
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`${meta.bg} ${meta.border} ${meta.text} gap-1 text-[10px]`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function FiscalNotificationsCenter({ restaurantId }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [resendingKey, setResendingKey] = useState(null);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const types = filterType === "all" ? "" : filterType;
      const statuses = filterStatus === "all" ? "" : filterStatus;
      const qs = new URLSearchParams();
      if (types) qs.append("types", types);
      if (statuses) qs.append("statuses", statuses);
      qs.append("limit", "100");
      const [feed, st] = await Promise.all([
        api.request(`/restaurants/${restaurantId}/fiscal-notifications?${qs}`),
        api.request(`/restaurants/${restaurantId}/fiscal-notifications/stats`),
      ]);
      setRows(feed.notifications || []);
      setStats(st);
    } catch (e) {
      toast.error(e?.message || "Error al cargar notificaciones");
    }
    finally { setLoading(false); }
  }, [restaurantId, filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (row) => {
    const key = `${row.type}-${row.ref_id}-${row.at}`;
    setResendingKey(key);
    try {
      if (row.type === "z_cert") {
        await api.resendZCertificate(restaurantId, row.ref_id);
      } else if (row.type === "annual_book") {
        await api.sendAnnualZBook(restaurantId, { year: parseInt(row.ref_id, 10) });
      } else if (row.type === "monthly_summary") {
        const [y, m] = (row.ref_id || "").split("-").map((s) => parseInt(s, 10));
        await api.sendMonthlyExecSummary(restaurantId, { year: y, month: m });
      }
      toast.success("Reenviado correctamente");
      await load();
    } catch (e) {
      toast.error(e?.message || "Error al reenviar");
    }
    finally { setResendingKey(null); }
  };

  const handleDismiss = async (row) => {
    try {
      await api.acknowledgeFiscalNotification(restaurantId, {
        type: row.type, refId: row.ref_id,
      });
      toast.success("Descartado · ya no aparecerá en alertas");
      await load();
    } catch (e) {
      toast.error(e?.message || "Error al descartar");
    }
  };

  const deliverabilityPct = useMemo(() => {
    if (!stats || stats.deliverability == null) return null;
    return (stats.deliverability * 100).toFixed(0);
  }, [stats]);

  return (
    <Card data-testid="fiscal-notifications-center">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Bell className="h-4 w-4 text-violet-600" />
            Centro de notificaciones fiscales
          </CardTitle>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1"
            onClick={load}
            disabled={loading}
            data-testid="fiscal-notifications-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* KPIs */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="fiscal-notifications-kpis">
            <div className="rounded border bg-card p-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total envíos</div>
              <div className="text-lg font-semibold tabular-nums">{stats.total}</div>
            </div>
            <div className="rounded border bg-emerald-50 p-2">
              <div className="text-[10px] uppercase tracking-wide text-emerald-700">Entregabilidad</div>
              <div className="text-lg font-semibold tabular-nums text-emerald-800">
                {deliverabilityPct != null ? `${deliverabilityPct}%` : "—"}
              </div>
            </div>
            <div className="rounded border bg-rose-50 p-2">
              <div className="text-[10px] uppercase tracking-wide text-rose-700">Fallidos</div>
              <div className="text-lg font-semibold tabular-nums text-rose-800">{stats.total_failed}</div>
            </div>
            <div className="rounded border bg-amber-50 p-2">
              <div className="text-[10px] uppercase tracking-wide text-amber-700">Omitidos</div>
              <div className="text-lg font-semibold tabular-nums text-amber-800">{stats.total_skipped}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="fiscal-notifications-filters">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1">Tipo:</span>
          {[
            ["all", "Todos"],
            ["z_cert", "Certificado Z"],
            ["annual_book", "Libro anual"],
            ["monthly_summary", "Resumen mensual"],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              className={`text-[11px] px-2 py-0.5 rounded-full border transition ${filterType === v ? "bg-violet-100 border-violet-300 text-violet-800" : "bg-card border-border text-muted-foreground hover:bg-gray-50"}`}
              onClick={() => setFilterType(v)}
              data-testid={`filter-type-${v}`}
            >{label}</button>
          ))}
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground ml-2 mr-1">Estado:</span>
          {[
            ["all", "Todos"],
            ["sent", "Enviados"],
            ["failed", "Fallidos"],
            ["skipped", "Omitidos"],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              className={`text-[11px] px-2 py-0.5 rounded-full border transition ${filterStatus === v ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-card border-border text-muted-foreground hover:bg-gray-50"}`}
              onClick={() => setFilterStatus(v)}
              data-testid={`filter-status-${v}`}
            >{label}</button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-6 text-[11px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Cargando…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Sin notificaciones fiscales todavía.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[500px] overflow-y-auto" data-testid="fiscal-notifications-list">
            {rows.map((r, i) => {
              const meta = TYPE_META[r.type] || {};
              const Icon = meta.icon || Bell;
              const key = `${r.type}-${r.ref_id}-${r.at}-${i}`;
              const canResend = r.type === "z_cert" || r.type === "annual_book" || r.type === "monthly_summary";
              const busy = resendingKey === `${r.type}-${r.ref_id}-${r.at}`;
              return (
                <div
                  key={key}
                  className="flex items-center gap-3 p-2.5 rounded border bg-card hover:bg-gray-50/40 transition"
                  data-testid={`notification-row-${r.type}-${i}`}
                >
                  <div
                    className="h-8 w-8 rounded-md flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${meta.color}18` }}
                  >
                    <Icon className="h-4 w-4" style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-foreground truncate">{r.ref_label}</span>
                      <StatusBadge status={r.status} />
                      {r.trigger === "auto" && (
                        <span className="text-[9px] uppercase tracking-wide text-muted-foreground">auto</span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {r.to ? <span className="font-mono">{r.to}</span> : "—"}
                      {r.subject && <span className="mx-1.5">·</span>}
                      {r.subject && <span className="italic">{r.subject}</span>}
                    </div>
                    {r.reason && r.status !== "sent" && (
                      <div className="text-[10px] text-rose-700 mt-0.5 truncate">{r.reason}</div>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground text-right shrink-0 min-w-[90px]">
                    {fmtDate(r.at)}
                  </div>
                  {canResend && r.status !== "sent" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1"
                      onClick={() => handleResend(r)}
                      disabled={busy}
                      data-testid={`notification-resend-${r.type}-${i}`}
                    >
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                      Reenviar
                    </Button>
                  )}
                  {r.status === "failed" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1 text-muted-foreground hover:text-gray-700"
                      onClick={() => handleDismiss(r)}
                      title="Descartar de alertas (sin reenviar)"
                      data-testid={`notification-dismiss-${r.type}-${i}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
